import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { withReferral } from "../utils/withReferral";
import { useProfile } from "../contexts/ProfileContext";
import { usePageTracking, useAnalytics } from "../hooks/useAnalytics";
import LoadingScreen from "../components/LoadingScreen";
import { showToast } from "../components/Toast";
import VideoPlayer from "../components/VideoPlayer";
import ShareModal from "../components/ShareModal";
import ShareVideoModal from "../components/ShareVideoModal";
import VideoWatermarkProgressModal from "../components/VideoWatermarkProgressModal";
import RecommendedStories from "../components/RecommendedStories";
import FeedbackModal from "../components/FeedbackModal";
import PaywallModal from "../components/PaywallModal";
import { useEntitlementGate } from "../contexts/EntitlementGateContext";
import LikeButton from "../components/common/LikeButton";
import logger from "../utils/logger";
import { supabase } from "../lib/supabaseClient";
import {
  clampStoryResumeSeconds,
  normalizeStoryResumeSeconds,
} from "../utils/progressStats";
import { updateStoryProgress } from "../utils/progressTracking";
import { getPersonalizedStoryDescription } from "../utils/storyDescription";
import { getStoryGenerationEstimate } from "../utils/storyEstimate";
import { useStorySubtitles } from "../hooks/useStorySubtitles";
import { useSubtitleControls } from "../hooks/useSubtitleControls";
import SubtitleTracks from "../components/SubtitleTracks";
import InlineVideoControls from "../components/InlineVideoControls";
import StoryverseModerationToggle from "../components/story/StoryverseModerationToggle";

// Session-level cache for feedback state (persists across component re-mounts)
let feedbackModalCache = {
  hasBeenShown: false,
  initialized: false,
};

// Video frame background — matches the page so any letterbox blends in.
const FRAME_PAGE_BG = "linear-gradient(180deg,#F5FAFF 0%,#EAF3FF 100%)";

const StoryDetail = ({ isStoryverse = false, isPublic = false }) => {
  usePageTracking("Story Detail", { isStoryverse, isPublic });
  const { trackContentView, trackContentShare, trackError, trackContentDeleted, trackVisibilityToggled } = useAnalytics();
  const { id: storyId } = useParams();
  const { apiClient } = useAPI();
  const { user, costs, updateUser, subscriptionStatus, isAuthenticated } = useAuth();
  const { requireEntitlement } = useEntitlementGate();
  const { requireParentalVerification } = useProfile();
  const isLoggedIn = !!user;
  const navigate = useNavigate();

  // Extract access token from URL query parameters (for magic link from email)
  const urlParams = new URLSearchParams(window.location.search);
  const accessToken = urlParams.get('token');
  const fromWeeklyChallenge =
    urlParams.get("fromWeeklyChallenge") === "1" ||
    urlParams.get("fromWeeklyChallenge") === "true";
  const resumeParam = Number(urlParams.get("resume") || "0");
  const resumeSeconds = Number.isFinite(resumeParam) && resumeParam > 0 ? resumeParam : 0;

  const [story, setStory] = useState(null);
  const { subtitleUrls, requestSubtitle, loadingLang } = useStorySubtitles(story?.id, story);
  const [loading, setLoading] = useState(true);
  const [isVideoPlayerOpen, setIsVideoPlayerOpen] = useState(false);
  const [isPlayingInline, setIsPlayingInline] = useState(false); // New state for inline playback
  const [charactersData, setCharactersData] = useState([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareVideoModalOpen, setIsShareVideoModalOpen] = useState(false);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [isDownloadProgressModalOpen, setIsDownloadProgressModalOpen] =
    useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showFeedbackNotification, setShowFeedbackNotification] =
    useState(false);
  const [isPaywallModalOpen, setIsPaywallModalOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const subtitleControls = useSubtitleControls(videoRef, subtitleUrls, {
    enabled: Boolean(story?.id && story?.file_url),
    requestSubtitle,
    loadingLang,
    nativeCaptions: false, // we render captions ourselves so they never bleed past the frame
  });
  const [videoRatio, setVideoRatio] = useState(null); // intrinsic w/h, to detect portrait
  const hasTrackedInlinePlay = useRef(false);
  const trackedStoryId = useRef(null);
  const hasAppliedResumeRef = useRef(false);
  const lastStoryProgressSentAtRef = useRef(0);
  const hasStoryFinishedRef = useRef(false);
  const hasCompletedWeeklyChallengeRef = useRef(false);
  const shareMenuRef = useRef(null);
  const feedbackNotificationTimeoutRef = useRef(null);
  const videoSessionId = useRef(null); // For feedback modal
  const [showInlineControls, setShowInlineControls] = useState(true);
  const inlineControlsTimeoutRef = useRef(null);

  const revealInlineControls = useCallback((autoHide = true) => {
    setShowInlineControls(true);
    if (inlineControlsTimeoutRef.current) {
      clearTimeout(inlineControlsTimeoutRef.current);
      inlineControlsTimeoutRef.current = null;
    }

    if (!autoHide) return;

    const video = videoRef.current;
    if (video && !video.paused) {
      inlineControlsTimeoutRef.current = setTimeout(() => {
        setShowInlineControls(false);
      }, 3000);
    }
  }, []);

  useEffect(() => {
    if (subtitleControls.showCCMenu) {
      setShowInlineControls(true);
      if (inlineControlsTimeoutRef.current) {
        clearTimeout(inlineControlsTimeoutRef.current);
        inlineControlsTimeoutRef.current = null;
      }
    }
  }, [subtitleControls.showCCMenu]);

  useEffect(() => {
    if (!isPlayingInline) {
      setShowInlineControls(true);
    }
    return () => {
      if (inlineControlsTimeoutRef.current) {
        clearTimeout(inlineControlsTimeoutRef.current);
      }
    };
  }, [isPlayingInline]);

  const applyResumeToVideo = () => {
    if (!videoRef.current || hasAppliedResumeRef.current || resumeSeconds <= 0) return;
    const duration = Number(videoRef.current.duration || 0);

    if (duration <= 0) {
      try {
        videoRef.current.currentTime = Math.max(0, Number(resumeSeconds) || 0);
      } catch {
        // ignore until metadata is available
      }
      return;
    }

    const normalized = normalizeStoryResumeSeconds(resumeSeconds, { duration_seconds: duration });
    const clamped = clampStoryResumeSeconds(normalized, duration);
    try {
      videoRef.current.currentTime = clamped;
      hasAppliedResumeRef.current = true;
    } catch (error) {
      logger.warn("Failed applying resume time to video", error);
    }
  };

  const trackInlineStoryProgress = (status = "IN_PROGRESS") => {
    console.log("trackInlineStoryProgress triggered", {
      status,
      storyId: story?.id,
      userId: user?.id
    });
    if (!videoRef.current || !user?.id || !apiClient || !story?.id) return;
    if (hasStoryFinishedRef.current && status !== "FINISHED") return;

    const now = Date.now();
    const duration = Number(videoRef.current.duration || 0);
    const current = Number(videoRef.current.currentTime || 0);
    const normalizedProgress =
      status === "FINISHED"
        ? Math.max(duration, current)
        : clampStoryResumeSeconds(current, duration);

    if (status === "IN_PROGRESS") {
      if (normalizedProgress <= 0) return;
      if (now - lastStoryProgressSentAtRef.current < 5000) return;
    }

    lastStoryProgressSentAtRef.current = now;
    if (status === "FINISHED") {
      hasStoryFinishedRef.current = true;
    }

    console.log("Sending story progress to backend", {
      storyId: story?.id,
      status
    });
    updateStoryProgress(apiClient, user.id, story.id, normalizedProgress, status)
      .then(async (didUpdate) => {
        if (!didUpdate) return;

        try {
          const statsResponse = await apiClient.getUserStats(user.id);
          if (statsResponse?.status && statsResponse?.data) {
            updateUser({
              ...user,
              stats: statsResponse.data,
            });
          }
        } catch (error) {
          logger.warn("Failed to refresh user stats after story completion", error);
        }
      })
      .catch((error) => {
        logger.warn("Failed to persist story progress update", error);
      });
  };

  // Initialize video session ID
  useEffect(() => {
    if (!videoSessionId.current) {
      videoSessionId.current = uuidv4();
    }
  }, []);

  useEffect(() => {
    hasStoryFinishedRef.current = false;
    lastStoryProgressSentAtRef.current = 0;
    hasCompletedWeeklyChallengeRef.current = false;
  }, [story?.id]);

  const handleWeeklyChallengeCompletion = async () => {
    if (!fromWeeklyChallenge || !user?.id || !story?.id) return;
    if (hasCompletedWeeklyChallengeRef.current) return;

    hasCompletedWeeklyChallengeRef.current = true;

    window.dispatchEvent(new Event("weeklyChallengeCompleted"));
  };

  // Initialize feedback cache from user's data_tags
  useEffect(() => {
    if (user?.id && !feedbackModalCache.initialized) {
      const userDataTags = user.data_tags || [];
      const hasSeenInBackend = userDataTags.some((tag) =>
        tag.startsWith("FEEDBACK_MODAL_SHOWN"),
      );

      feedbackModalCache.hasBeenShown = hasSeenInBackend;
      feedbackModalCache.initialized = true;

      logger.log(
        `📋 Feedback cache initialized from backend (users.data_tags):`,
        {
          hasBeenShown: feedbackModalCache.hasBeenShown,
          userId: user.id,
          dataTags: userDataTags,
        },
      );
    }
  }, [user]);

  // Poll for story updates if processing
  useEffect(() => {
    if (!story) return;

    const isFailed = story.status === "FAILED" || story.status === "failed";
    const isProcessing =
      !isFailed &&
      (story.status === "PROCESSING" ||
        story.status === "requested" ||
        story.status === "pending" ||
        story.status === "REQUESTED" ||
        story.status === "PENDING" ||
        !story.file_url);

    if (!isProcessing) return;

    const pollInterval = setInterval(async () => {
      try {
        logger.log("🔄 Polling for story updates...");
        const response = isStoryverse
          ? await apiClient.getStoriverseEpisode(storyId)
          : await apiClient.getStory(storyId, accessToken, true);
        if (response.status && response.data) {
          logger.log("📊 Polling response story:", response.data);

          // Email notification is now handled by video driver on completion
          setStory(response.data);
        }
      } catch (error) {
        logger.error("Error polling story updates:", error);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [story, storyId, apiClient, isStoryverse, accessToken]);

  useEffect(() => {
    const loadData = async () => {
      if (!apiClient || !storyId) return;

      // Prevent re-fetch when tab is hidden (e.g., user opened social share in new tab)
      // This ensures no API calls when user switches tabs
      if (document.visibilityState === "hidden") {
        logger.log("Tab is hidden, skipping data load to preserve state");
        return;
      }

      try {
        setLoading(true);

        // Load story details
        const storyResponse = isPublic
          ? await apiClient.getSharedEpisode(storyId, accessToken)
          : isStoryverse
            ? await apiClient.getStoriverseEpisode(storyId)
            : await apiClient.getStory(storyId, accessToken);
        if (storyResponse.status) {
          const storyData = storyResponse.data;
          setStory(storyData);

          // Track story/episode view
          trackContentView(
            isStoryverse ? "storyverse_content" : "story",
            storyId,
          );

          // Shared/public responses embed character summaries (backend 1A), so
          // use those directly — the per-character /characters/{id} calls below
          // are now token-gated and would 403 for anonymous share viewers.
          if (Array.isArray(storyData.character_details) && storyData.character_details.length > 0) {
            setCharactersData(storyData.character_details);
          } else if (storyData.characters && storyData.characters.length > 0) {
            console.log(
              "🔍 CHARACTER DEBUG: Story characters found:",
              storyData.characters,
            );
            console.log("🔍 CHARACTER DEBUG: story.id:", storyData.id);
            console.log(
              "🔍 CHARACTER DEBUG: story.character_id:",
              storyData.character_id,
            );
            logger.log("🎭 Story characters found:", storyData.characters);

            const charactersPromises = storyData.characters.map(
              async (characterId) => {
                try {
                  if (typeof characterId === "string") {
                    console.log(
                      "🔍 CHARACTER DEBUG: Loading character details for ID:",
                      characterId,
                    );
                    logger.log(
                      "🔍 Loading character details for ID:",
                      characterId,
                    );
                    const charResponse =
                      await apiClient.getCharacter(characterId);
                    console.log(
                      "🔍 CHARACTER DEBUG: API response for character",
                      characterId,
                      ":",
                      charResponse,
                    );
                    if (charResponse.status) {
                      console.log(
                        "🔍 CHARACTER DEBUG: Character loaded successfully:",
                        charResponse.data,
                      );
                      console.log(
                        "🔍 CHARACTER DEBUG: Character name:",
                        charResponse.data?.name,
                      );
                      logger.log(
                        "✅ Character loaded successfully:",
                        charResponse.data,
                      );
                      return charResponse.data;
                    } else {
                      console.warn(
                        "🔍 CHARACTER DEBUG: Failed to load character, response:",
                        charResponse,
                      );
                      logger.warn(
                        "⚠️ Failed to load character, response:",
                        charResponse,
                      );
                      return null;
                    }
                  }
                  // If it's already an object
                  console.log(
                    "🔍 CHARACTER DEBUG: Character is already an object:",
                    characterId,
                  );
                  console.log(
                    "🔍 CHARACTER DEBUG: Character object name:",
                    characterId?.name,
                  );
                  logger.log("📋 Character is already an object:", characterId);
                  return characterId;
                } catch (error) {
                  console.error(
                    "🔍 CHARACTER DEBUG: Failed to load character:",
                    characterId,
                    error,
                  );
                  logger.error(
                    "❌ Failed to load character:",
                    characterId,
                    error,
                  );
                  return null;
                }
              },
            );

            const loadedCharacters = await Promise.all(charactersPromises);
            const validCharacters = loadedCharacters.filter(
              (char) => char !== null,
            );
            console.log(
              "🔍 CHARACTER DEBUG: Final loaded characters:",
              validCharacters,
            );
            console.log(
              "🔍 CHARACTER DEBUG: Valid characters count:",
              validCharacters.length,
            );
            validCharacters.forEach((char, idx) => {
              console.log(`🔍 CHARACTER DEBUG: Character ${idx}:`, {
                id: char?.id,
                name: char?.name,
                hasName: !!char?.name,
              });
            });
            logger.log("🎭 Final loaded characters:", validCharacters);
            setCharactersData(validCharacters);
          } else {
            console.log(
              "🔍 CHARACTER DEBUG: No characters found in story data",
            );
            logger.log("📭 No characters found in story data");
          }
        } else {
          showToast(
            "Oops! The story didn't load this time. Let's try again!",
            "error",
          );
          // Use isStoryverse here since backToStoryverse isn't available yet
          navigate(isStoryverse ? "/storyverse" : "/library");
        }
      } catch (error) {
        logger.error("Failed to load story details:", error);
        showToast("Failed to load story details", "error");
        // Use isStoryverse here since backToStoryverse isn't available yet
        navigate(isStoryverse ? "/storyverse" : "/library");
      } finally {
        setLoading(false);
      }
    };

    if (storyId) {
      loadData();
    }
  }, [storyId, apiClient, isStoryverse, navigate, accessToken]);

  // Track visits to shared story pages
  useEffect(() => {
    if (isPublic && story && storyId && apiClient && user?.id) {
      const timestamp = new Date().toISOString();
      const tag = `VISITEDSHARESTORY_${storyId}_${timestamp}`;

      apiClient
        .addUserDataTag(user.id, tag)
        .then((result) => {
          if (result.status) {
            logger.log(
              `✅ Added visited share story tag ${tag} for user ${user.id}`,
            );
          } else {
            logger.warn(
              `⚠️ Failed to add visited share story tag ${tag}:`,
              result.error,
            );
          }
        })
        .catch((error) => {
          logger.error(
            `❌ Error adding visited share story tag ${tag}:`,
            error,
          );
        });
    }
  }, [isPublic, story, storyId, apiClient, user?.id]);

  // Pause video when tab becomes hidden and restore state when visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (videoRef.current) {
        if (document.hidden) {
          // Tab is hidden - save state and pause video
          try {
            const currentTime = videoRef.current.currentTime || 0;
            const isPaused = videoRef.current.paused;
            localStorage.setItem(
              "pixley_video_state",
              JSON.stringify({
                currentTime,
                isPaused,
                timestamp: Date.now(),
              }),
            );
            videoRef.current.pause();
            logger.log("Video paused and state saved:", {
              currentTime,
              isPaused,
            });
          } catch (error) {
            logger.error("Failed to save video state:", error);
            videoRef.current.pause();
          }
        } else {
          // Tab becomes visible - restore state but keep paused
          try {
            const savedState = localStorage.getItem("pixley_video_state");
            if (savedState) {
              const { currentTime, isPaused, timestamp } =
                JSON.parse(savedState);
              // Only restore if saved within last 5 minutes
              if (Date.now() - timestamp < 5 * 60 * 1000) {
                videoRef.current.currentTime = currentTime;
                // Always keep paused when returning - don't auto-play
                videoRef.current.pause();
                logger.log("Video state restored, kept paused:", {
                  currentTime,
                });
                // Clear saved state after restoring
                localStorage.removeItem("pixley_video_state");
              }
            }
          } catch (error) {
            logger.error("Failed to restore video state:", error);
            // Ensure video is paused even if restore fails
            videoRef.current.pause();
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Listen for share modal open event from VideoPlayer
  useEffect(() => {
    const handleOpenShareModal = () => {
      setIsShareModalOpen(true);
    };

    window.addEventListener("openShareModal", handleOpenShareModal);

    return () => {
      window.removeEventListener("openShareModal", handleOpenShareModal);
    };
  }, []);

  // Auto-open and play video when story loads (if autoplay flag is set)
  useEffect(() => {
    if (!story?.file_url) return;

    const autoPlayFromSession =
      sessionStorage.getItem("autoplay_video_story") === "true";
    const shouldAutoPlay = autoPlayFromSession || resumeSeconds > 0;
    if (!shouldAutoPlay) return;

    // Remove flag immediately to prevent re-triggering
    if (autoPlayFromSession) {
      sessionStorage.removeItem("autoplay_video_story");
    }

    // Auto-play inline instead of opening modal
    if (story.file_url && !isPlayingInline) {
      setIsPlayingInline(true);
      // Auto-play the video inline
      setTimeout(() => {
        if (videoRef.current) {
          applyResumeToVideo();
          videoRef.current.play().catch((err) => {
            logger.log("Autoplay prevented:", err);
          });
        }
      }, 100);
      logger.log("✅ Auto-playing video inline");
    }
  }, [story?.file_url, story?.id, isPlayingInline, resumeSeconds]); // Re-run when story or video URL changes

  // Format time helper

  // Inline video tracking — views, WATCH tag, video_events
  const trackInlinePlay = async () => {
    if (!storyId) return;
    // Reset if navigated to a different story
    if (trackedStoryId.current !== storyId) {
      hasTrackedInlinePlay.current = false;
      trackedStoryId.current = storyId;
    }
    if (hasTrackedInlinePlay.current) return;
    hasTrackedInlinePlay.current = true;

    // Increment views on stories table
    try {
      const { data: row } = await supabase
        .from('stories')
        .select('views, private_views')
        .eq('id', storyId)
        .maybeSingle();

      if (row) {
        await supabase
          .from('stories')
          .update({
            views: (Number(row.views) || 0) + 1,
            private_views: (Number(row.private_views) || 0) + 1,
          })
          .eq('id', storyId);
        console.log(`✅ Incremented views for story ${storyId}`);
      }
    } catch (err) {
      console.error('❌ Error incrementing views:', err);
    }

    // Add WATCH tag
    if (user?.id && apiClient) {
      const tag = `WATCH_${storyId}`;
      apiClient.addUserDataTag(user.id, tag).then(() => {
        console.log(`✅ Added WATCH tag: ${tag}`);
      }).catch(() => {});
    }

    // Log video_event (play)
    if (!videoSessionId.current) videoSessionId.current = uuidv4();
    try {
      await supabase.from('video_events').insert({
        user_id: user?.id || 'guest_user',
        video_id: storyId,
        session_id: videoSessionId.current,
        data_tags: { events: [{ type: 'play', position: 0 }] },
      });
      console.log(`✅ Logged video_event for story ${storyId}`);
    } catch (err) {
      console.error('❌ Error logging video_event:', err);
    }
  };

  const handlePlayStory = () => {
    if (story.file_url) {
      setIsPlayingInline(true); // Play inline instead of opening modal

      // Auto-play the video after state update
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.muted = false; // Unmute for playback
          videoRef.current.play().catch((err) => {
            logger.log("Play prevented:", err);
            // If play is blocked, try muted
            videoRef.current.muted = true;
            videoRef.current.play();
          });
        }
      }, 100);
    } else {
      showToast("Story is still processing...", "info");
    }
  };

  const handleVideoEnd = () => {
    logger.log("🎬 Video ended in StoryDetail (inline playback)");
    console.log("handleVideoEnd called");
    console.log("Story ID:", story?.id);
    console.log("User ID:", user?.id);
    console.log("Calling trackInlineStoryProgress with FINISHED");
    trackInlineStoryProgress("FINISHED");
    handleWeeklyChallengeCompletion();

    // Pause the video
    if (videoRef.current) {
      videoRef.current.pause();
    }

    // FIRST: Show share modal immediately
    setIsShareVideoModalOpen(true);

    // THEN: Check if user should see feedback modal (after share modal closes)
    if (user?.id) {
      logger.log(`🎬 Video ended. Checking feedback eligibility:`, {
        userId: user.id,
        cacheHasBeenShown: feedbackModalCache.hasBeenShown,
        cacheInitialized: feedbackModalCache.initialized,
      });

      // Check session cache FIRST (prevents multiple shows in same session)
      if (feedbackModalCache.hasBeenShown) {
        logger.log(
          "🔔 Returning user: showing feedback notification (2s delay after share modal)",
        );

        if (feedbackNotificationTimeoutRef.current) {
          clearTimeout(feedbackNotificationTimeoutRef.current);
        }

        feedbackNotificationTimeoutRef.current = setTimeout(() => {
          if (!showFeedbackModal) {
            setShowFeedbackNotification(true);
            logger.log("📬 Small feedback notification shown");
          }
          feedbackNotificationTimeoutRef.current = null;
        }, 2000);

        return;
      }

      // Double-check backend as final safety (in case cache wasn't initialized)
      const userDataTags = user.data_tags || [];
      const hasSeenInBackend = userDataTags.some((tag) =>
        tag.startsWith("FEEDBACK_MODAL_SHOWN"),
      );

      if (hasSeenInBackend) {
        logger.log(
          "🔔 Returning user (backend check): showing feedback notification (2s delay)",
        );
        feedbackModalCache.hasBeenShown = true; // Update cache

        if (feedbackNotificationTimeoutRef.current) {
          clearTimeout(feedbackNotificationTimeoutRef.current);
        }

        feedbackNotificationTimeoutRef.current = setTimeout(() => {
          if (!showFeedbackModal) {
            setShowFeedbackNotification(true);
            logger.log("📬 Small feedback notification shown");
          }
          feedbackNotificationTimeoutRef.current = null;
        }, 2000);

        return;
      }

      // First-time user: show full feedback modal (after share modal closes)
      logger.log(
        "🆕 First-time user: will show full feedback modal after share modal closes",
      );

      // IMMEDIATELY mark as shown in cache (prevents duplicate triggers)
      feedbackModalCache.hasBeenShown = true;

      // Feedback modal will be shown when share modal closes
      // (handled in share modal's onClose)
    }
  };

  const closeVideoPlayer = () => {
    setIsVideoPlayerOpen(false);
  };

  const handleDeleteStory = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this story? This action cannot be undone.",
      )
    ) {
      return;
    }

    // Optimistically clear story state
    const storyToDelete = story;
    setStory(null);

    try {
      const response = await apiClient.deleteStory(storyId);
      if (response.status) {
        trackContentDeleted('story', storyId);
        showToast("Story deleted successfully", "success");
        // Dispatch event to notify Stories page to refresh
        window.dispatchEvent(
          new CustomEvent("story:deleted", { detail: { storyId } }),
        );
        // Only owners can delete, so always go back to their stories page
        navigate("/library");
      } else {
        // Revert optimistic update on failure
        setStory(storyToDelete);
        showToast("Failed to delete story", "error");
      }
    } catch (error) {
      // Revert optimistic update on error
      setStory(storyToDelete);
      logger.error("Failed to delete story:", error);
      showToast("Failed to delete story", "error");
    }
  };

  const handleShare = async () => {
    trackContentShare(isStoryverse ? "storyverse_content" : "story", storyId);
    // Ensure the story has a share_token so the link actually resolves
    // (private-but-linkable). Owners only; no-op if already shared.
    if (isOwner && story && !story.share_token) {
      try {
        const resp = await apiClient.setStorySharing(storyId, true);
        if (resp?.status && resp?.data?.share_token) {
          setStory({ ...story, share_token: resp.data.share_token });
        }
      } catch (e) {
        logger.error("Failed to enable story sharing:", e);
      }
    }
    setIsShareModalOpen(true);
  };

  const handleToggleVisibility = async () => {
    if (!story || isUpdatingVisibility) return;

    const newVisibility = !story.is_public;
    setIsUpdatingVisibility(true);

    try {
      const response = await apiClient.updateStoryVisibility(
        storyId,
        newVisibility,
      );
      if (response.status) {
        trackVisibilityToggled('story', storyId, newVisibility);
        setStory({ ...story, is_public: newVisibility });
        showToast(
          `Story is now ${newVisibility ? "public" : "private"}`,
          "success",
        );
      } else {
        showToast("Failed to update story visibility", "error");
      }
    } catch (error) {
      logger.error("Failed to update story visibility:", error);
      showToast("Failed to update story visibility", "error");
    } finally {
      setIsUpdatingVisibility(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading story..." type="story" />;
  }

  if (!story) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)" }}>
        <div className="text-center">
          <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 28, fontWeight: 700, color: "#0B1A33", marginBottom: 12 }}>
            Story not found
          </h2>
          <button
            onClick={() => navigate(isStoryverse ? "/storyverse" : "/library")}
            style={{ padding: "0 24px", height: 48, background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", color: "#FFF", border: "none", borderRadius: 18, fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0px 4px 0px #073B8E", fontFamily: "'Fredoka', sans-serif" }}
          >
            Back to {isStoryverse ? "Storyverse" : "Stories"}
          </button>
        </div>
      </div>
    );
  }

  const hasVideoFile = story.file_url && story.file_url.trim() !== "";
  const isFailed = story.status === "FAILED" || story.status === "failed";
  const isProcessing =
    !isFailed &&
    (story.status === "PROCESSING" ||
      story.status === "requested" ||
      story.status === "pending" ||
      story.status === "REQUESTED" ||
      story.status === "PENDING" ||
      !hasVideoFile);

  // Check if current user owns this story
  const isOwner = user && story && story.user_id === user.id;

  // If story doesn't belong to current user, it's a community story from storyverse
  const isCommunityStory = !isOwner && !isPublic;

  // Determine if we should navigate back to storyverse instead of stories
  const backToStoryverse = isStoryverse || isCommunityStory;

  // User can only edit/delete if:
  // 1. Not a storyverse story (community content)
  // 2. Not viewing as public share link
  // 3. User is the owner of the story
  const canEdit = !isStoryverse && !isPublic && isOwner;

  // Check if download button should be shown (same as bedtime - show to everyone when video is ready)
  const canShowDownloadButton = hasVideoFile && !isProcessing;

  // Check if user is premium
  const isPremium = costs?.downloadAbility === true;
  console.log("HELLOOOOO");
  console.log(costs);

  const handleDownloadStory = () => {
    if (!storyId) return;

    // Members-only gate first (no-op pre-cutover).
    if (!requireEntitlement({ trigger: "download", contentType: "story" })) {
      return;
    }

    // Check if user is premium
    if (!isPremium) {
      // Show paywall modal for non-premium users
      logger.log("Free user attempting video download - showing paywall");
      setIsPaywallModalOpen(true);
      return;
    }

    // Premium user - proceed with download
    logger.log("Premium user - downloading watermarked video");
    setIsDownloadProgressModalOpen(true);

    // Track download
    supabase.from('stories').select('download_count').eq('id', storyId).maybeSingle()
      .then(({ data, error }) => {
        console.log('📥 Download track - select result:', { data, error, storyId });
        if (data) {
          supabase.from('stories').update({ download_count: (Number(data.download_count) || 0) + 1 }).eq('id', storyId)
            .then(({ data: updated, error: updateErr }) => {
              console.log('📥 Download track - update result:', { updated, updateErr });
            });
        }
      });
  };

  /* ── Shared button styles for the Pixley design system ── */
  const pillBtn = "flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-all duration-150";
  const secondaryPill = `${pillBtn} bg-white border-2 border-[rgba(0,101,248,0.12)] text-[#0B1A33] shadow-[0px_3px_0px_rgba(11,26,51,0.08)] hover:-translate-y-px hover:shadow-[0px_4px_0px_rgba(11,26,51,0.10)] active:translate-y-[2px] active:shadow-[0px_1px_0px_rgba(11,26,51,0.08)]`;
  const primaryPill = `${pillBtn} text-white shadow-[0px_3px_0px_#073B8E] hover:-translate-y-px hover:shadow-[0px_4px_0px_#073B8E] active:translate-y-[2px] active:shadow-[0px_1px_0px_#073B8E]`;
  const dangerPill = `${pillBtn} text-[#E53E3E] bg-[#FFE9EE] border-2 border-[rgba(229,62,62,0.15)] shadow-[0px_3px_0px_rgba(229,62,62,0.12)] hover:-translate-y-px active:translate-y-[2px] active:shadow-none`;

  // Use the video's REAL ratio (metadata aspect_ratio is unreliable — often
  // wrongly 16:9). Portrait videos hug the frame (shadow + rounded corners right
  // on the video, no side matte); landscape uses a full-width 16:9 frame whose
  // letterbox (for square clips) is filled with the page background so it blends.
  const realRatio = Number.isFinite(videoRatio) && videoRatio > 0 ? videoRatio : null;
  const isPortrait = realRatio != null ? realRatio < 1 : story?.aspect_ratio === "9:16";
  const frameAspect = isPortrait ? realRatio || "9 / 16" : "16 / 9";
  const frameMaxWidth = isPortrait ? 400 : undefined;
  const frameBg = FRAME_PAGE_BG;

  return (
    <div className="story-detail-container min-h-screen" style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)", fontFamily: "'Inter', 'Neue Montreal', 'Fredoka', sans-serif" }}>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-28">
        {/* Header — back + action buttons */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          {/* Back button */}
          <button
            onClick={() => navigate(isPublic ? "/" : backToStoryverse ? "/storyverse" : "/library")}
            className={secondaryPill}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
            <span className="hidden sm:inline">{isPublic ? "Go to Pixley" : `Back to ${backToStoryverse ? "Storyverse" : "Stories"}`}</span>
          </button>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {story?.id && isAuthenticated && <LikeButton contentType="video" contentId={story.id} />}

            <button onClick={handleShare} className={secondaryPill}>
              <svg className="w-4 h-4" fill="none" stroke="#0065F8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
              <span className="hidden sm:inline">Copy Link</span>
            </button>

            {hasVideoFile && !isProcessing && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsShareVideoModalOpen(true); }}
                className={`${primaryPill} bg-gradient-to-b from-[#3EDC8F] to-[#20A96A]`}
                style={{ boxShadow: "0px 3px 0px #178F56" }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                Share to Social
              </button>
            )}

            {canShowDownloadButton && (
              <button
                onClick={handleDownloadStory}
                className={`${primaryPill} bg-gradient-to-b from-[#0A7DFF] to-[#0065F8]`}
              >
                {!isPremium && <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C9.243 2 7 4.243 7 7v3H6c-1.103 0-2 .897-2 2v8c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2v-8c0-1.103-.897-2-2-2h-1V7c0-2.757-2.243-5-5-5zM9 7c0-1.654 1.346-3 3-3s3 1.346 3 3v3H9V7z" /></svg>}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                <span className="hidden sm:inline">Download{!isPremium && " (Premium)"}</span>
              </button>
            )}

            {canEdit && (
              <button
                onClick={() => requireParentalVerification('delete this story', handleDeleteStory)}
                className={dangerPill}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                <span className="hidden sm:inline">Delete</span>
              </button>
            )}

            {/* Internal moderation — also in the main action row so it's hard to miss */}
            <StoryverseModerationToggle
              storyId={storyId}
              enabled={isLoggedIn && !isPublic}
              canModerateStoryverse={
                story?.can_moderate_storyverse === true
                  ? true
                  : story?.can_moderate_storyverse === false
                    ? false
                    : undefined
              }
              showInStoryverse={story?.show_in_storyverse}
            />

            {isPublic && (
              <button
                onClick={() => navigate("/")}
                className={`${primaryPill} bg-gradient-to-b from-[#0A7DFF] to-[#0065F8]`}
              >
                Create Your Own Story
              </button>
            )}
          </div>
        </div>

        {/* Story Content — 2 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 lg:items-start">
          {/* LEFT COLUMN — Video */}
          <div className="space-y-6">
            {/* Structural container only — the card chrome (radius + shadow) lives on
                the frame below so it hugs the actual video (esp. 9:16 portrait). */}
            <div className="relative">
              <div
                ref={videoContainerRef}
                className="relative overflow-hidden mx-auto"
                onMouseMove={() => isPlayingInline && revealInlineControls()}
                onMouseLeave={() => {
                  if (isPlayingInline && videoRef.current && !videoRef.current.paused) {
                    setShowInlineControls(false);
                  }
                }}
                style={{
                  aspectRatio: frameAspect,
                  maxWidth: frameMaxWidth,
                  borderRadius: 20,
                  boxShadow: "0px 2px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.10)",
                  background: frameBg,
                }}
              >
                {hasVideoFile && !isProcessing ? (
                  <>
                    <video
                      ref={videoRef}
                      style={{ backgroundColor: "transparent", transform: "scale(1.03)", cursor: "pointer" }}
                      className="w-full h-full object-contain pixley-inline-video"
                      onClick={() => {
                        if (!isPlayingInline) return;
                        const v = videoRef.current;
                        if (!v) return;
                        if (v.paused) v.play().catch(() => {});
                        else v.pause();
                      }}
                      poster={story.thumbnail_url || story.cover_image_url || undefined}
                      muted={!isPlayingInline}
                      loop={!isPlayingInline}
                      controls={false}
                      playsInline
                      preload="metadata"
                      controlsList="nodownload"
                      disablePictureInPicture={false}
                      crossOrigin="anonymous"
                      onContextMenu={(e) => e.preventDefault()}
                      onLoadedMetadata={(e) => {
                        const video = e.target;
                        if (video.videoWidth && video.videoHeight) {
                          setVideoRatio(video.videoWidth / video.videoHeight);
                        }
                        if (fromWeeklyChallenge) {
                          video.currentTime = 0;
                          hasAppliedResumeRef.current = true;
                          return;
                        }
                        if (
                          video.duration &&
                          video.duration > 0 &&
                          !isPlayingInline
                        ) {
                          video.currentTime = video.duration / 2;
                        }
                        if (isPlayingInline) {
                          applyResumeToVideo();
                        }
                      }}
                      onEnded={() => {
                        console.log("🎬 Video onEnded triggered");
                        if (isPlayingInline) {
                          handleVideoEnd();
                        }
                      }}
                      onTimeUpdate={() => {
                        if (isPlayingInline) {
                          trackInlineStoryProgress("IN_PROGRESS");
                        }
                      }}
                      onPlay={() => {
                        logger.log("🎬 Video started playing inline");
                        trackInlinePlay();
                        revealInlineControls();
                      }}
                      onPause={() => {
                        setShowInlineControls(true);
                        if (inlineControlsTimeoutRef.current) {
                          clearTimeout(inlineControlsTimeoutRef.current);
                          inlineControlsTimeoutRef.current = null;
                        }
                      }}
                    >
                      <source src={story.file_url} type="video/mp4" />
                      <SubtitleTracks subtitleMap={subtitleControls.subtitleMap} />
                    </video>

                    {isPlayingInline && (
                      <InlineVideoControls
                        videoRef={videoRef}
                        containerRef={videoContainerRef}
                        subtitleControls={subtitleControls}
                        showSubtitles={Boolean(story?.id)}
                        visible={showInlineControls}
                      />
                    )}

                    {/* Custom-rendered captions — a frame-sized div so they never
                        bleed past the video edges (unlike native ::cue) */}
                    {subtitleControls.subtitlesEnabled && subtitleControls.activeCueText && (
                      <div
                        style={{
                          position: "absolute",
                          left: "50%",
                          bottom: isPlayingInline ? "4.25rem" : "1rem",
                          transform: "translateX(-50%)",
                          zIndex: 6,
                          maxWidth: "90%",
                          width: "max-content",
                          textAlign: "center",
                          whiteSpace: "pre-line",
                          background: "rgba(0,0,0,0.78)",
                          color: "#fff",
                          fontFamily: "Fredoka, Inter, sans-serif",
                          fontSize: "clamp(0.85rem, 1.6vw, 1.2rem)",
                          lineHeight: 1.35,
                          padding: "0.2em 0.55em",
                          borderRadius: 6,
                          pointerEvents: "none",
                        }}
                      >
                        {subtitleControls.activeCueText}
                      </div>
                    )}

                    {/* Caption-style "Loading" placeholder while subtitles generate */}
                    {subtitleControls.loadingLang && (
                      <div
                        aria-live="polite"
                        style={{
                          position: "absolute",
                          left: "50%",
                          bottom: "5rem",
                          transform: "translateX(-50%)",
                          zIndex: 5,
                          maxWidth: "92%",
                          textAlign: "center",
                          background: "rgba(0,0,0,0.78)",
                          color: "#fff",
                          fontFamily: "Fredoka, Inter, sans-serif",
                          fontSize: "clamp(0.85rem, 1.8vw, 1.05rem)",
                          lineHeight: 1.35,
                          padding: "0.15em 0.5em",
                          borderRadius: 4,
                          pointerEvents: "none",
                        }}
                      >
                        Loading…
                      </div>
                    )}

                    {/* Play button overlay */}
                    {!isPlayingInline && (
                      <div
                        className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-all duration-300 cursor-pointer"
                        onClick={handlePlayStory}
                      >
                        <button className="w-20 h-20 rounded-full flex items-center justify-center hover:scale-110 transition-all duration-200" style={{ background: "rgba(0,101,248,0.9)", boxShadow: "0px 4px 0px #073B8E, 0px 12px 32px rgba(0,101,248,0.3)" }}>
                          <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                        </button>
                      </div>
                    )}
                  </>
                ) : isFailed ? (
                  <div className="w-full h-full flex flex-col items-center justify-center px-4 text-center" style={{ background: "#FFE9EE" }}>
                    <div className="text-5xl mb-3">❌</div>
                    <p style={{ color: "#0B1A33", fontSize: 20, fontWeight: 700, fontFamily: "'Fredoka', sans-serif" }}>Story Failed</p>
                    <p style={{ color: "#334155", fontSize: 14, marginTop: 8 }}>Something went wrong while generating this story</p>
                    <button
                      type="button"
                      onClick={() => navigate("/create-story", { state: { description: getPersonalizedStoryDescription(story) || "", characters: story.characters || [] } })}
                      style={{ marginTop: 16, padding: "0 20px", height: 44, background: "linear-gradient(180deg, #FF6B6B 0%, #E53E3E 100%)", color: "#FFF", border: "none", borderRadius: 14, fontSize: 14, fontWeight: 700, cursor: "pointer", boxShadow: "0px 3px 0px #C53030" }}
                    >
                      Retry
                    </button>
                  </div>
                ) : isProcessing ? (
                  <div className="w-full h-full flex flex-col items-center justify-center" style={{ background: "linear-gradient(135deg, #EAF3FF, #E1EBFF)" }}>
                    <div className="relative w-24 h-24 mb-4">
                      <div className="absolute inset-0 border-4 rounded-full" style={{ borderColor: "rgba(0,101,248,0.15)" }}></div>
                      <div className="absolute inset-0 border-4 rounded-full animate-spin" style={{ borderColor: "#0065F8", borderRightColor: "transparent" }}></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span style={{ color: "#0065F8", fontSize: 14, fontWeight: 700 }}>{story.status_percentage || 0}%</span>
                      </div>
                    </div>
                    <p style={{ color: "#0B1A33", fontSize: 18, fontWeight: 600, fontFamily: "'Fredoka', sans-serif" }}>Processing your story...</p>
                    <p style={{ color: "#334155", fontSize: 14, marginTop: 8 }}>Usually ready in {getStoryGenerationEstimate(story.target_duration)}</p>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #EAF3FF, #E1EBFF)" }}>
                    <img
                      src={story.thumbnail_url || story.cover_image_url || "/assets/pixley-panda-nobg.png"}
                      alt={story.title}
                      className="w-full h-full object-contain"
                      onError={(e) => { e.target.src = "/assets/pixley-panda-nobg.png"; }}
                    />
                  </div>
                )}
              </div>

            </div>

            {/* Story Info — below the video card */}
            <div style={{ padding: "4px 0" }}>
              <h1 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: "clamp(1.5rem, 3.5vw, 2.2rem)", fontWeight: 700, color: "#0065F8", margin: "0 0 8px", lineHeight: 1.1 }}>
                {story.title || story.name || "Untitled Story"}
              </h1>
              {story.description && (
                <p style={{ fontSize: 15, color: "#334155", lineHeight: 1.55, margin: "0 0 16px", fontFamily: "'Inter', 'Neue Montreal', sans-serif" }}>
                  {getPersonalizedStoryDescription(story)}
                </p>
              )}

              {/* Metadata tags */}
              <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 12 }}>
                {story.theme && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, background: "#EAF3FF", color: "#0065F8", fontSize: 13, fontWeight: 700, boxShadow: "0px 2px 0px rgba(11,26,51,0.06)" }}>
                    {story.theme}
                  </span>
                )}
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, fontSize: 13, fontWeight: 700, boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                  background: isFailed ? "#FFE9EE" : isProcessing ? "#FFF8E7" : "#E6F7EE",
                  color: isFailed ? "#E53E3E" : isProcessing ? "#D39B22" : "#20A96A",
                }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: "currentColor" }} className={isProcessing ? "animate-pulse" : ""} />
                  {isFailed ? "Failed" : isProcessing ? "Processing" : "Ready"}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, background: "#EAF3FF", color: "#334155", fontSize: 13, fontWeight: 600, boxShadow: "0px 2px 0px rgba(11,26,51,0.06)" }}>
                  {story.updated_at
                    ? new Date(story.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                    : story.created_at
                      ? new Date(story.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : "Unknown"}
                </span>
                {canEdit && (
                  <button
                    onClick={handleToggleVisibility}
                    disabled={isUpdatingVisibility}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "none",
                      background: story?.is_public ? "#E6F7EE" : "#EAF3FF",
                      color: story?.is_public ? "#20A96A" : "#334155",
                      boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                    }}
                  >
                    {story?.is_public ? "🌍 Public" : "🔒 Private"}
                  </button>
                )}
              </div>

              {/* Characters */}
              {((charactersData && charactersData.length > 0) || (story.characters && story.characters.length > 0)) && (
                <div style={{ marginTop: 20 }}>
                  <h3 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 18, fontWeight: 700, color: "#0065F8", marginBottom: 10 }}>Characters</h3>
                  <div className="flex flex-wrap gap-2">
                    {(charactersData.length > 0 ? charactersData : story.characters).map((character, index) => {
                      const isObj = character && typeof character === "object";
                      const name = isObj ? (character.name || "Unknown Character") : "Unknown Character";
                      const avatar = isObj ? character.animated_image_url : null;
                      return (
                        <div key={isObj ? character.id : index} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", borderRadius: 999, background: "#EAF3FF", boxShadow: "0px 2px 0px rgba(11,26,51,0.06)" }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", overflow: "hidden", background: "linear-gradient(135deg, #0065F8, #00CAFF)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {avatar ? (
                              <img src={avatar} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
                            ) : (
                              <svg width="16" height="16" fill="none" stroke="#FFF" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                            )}
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 600, color: "#0B1A33" }}>{name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN — Recommended */}
          <div className="lg:pl-2 lg:sticky lg:top-24 lg:self-start">
            <RecommendedStories currentStoryId={storyId} />
          </div>
        </div>
      </div>

      {/* Video Player Modal */}
      <VideoPlayer
        isOpen={isVideoPlayerOpen && story.file_url}
        videoUrl={story.file_url}
        title={story.title || story.name || "Story"}
        onClose={closeVideoPlayer}
        storyId={story?.id}
        isStoryverse={isStoryverse}
        isCommunityStory={isCommunityStory}
      />

      {/* Share Modal */}
      {!isPublic && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          shareUrl={withReferral(`${window.location.origin}/share/episode/${storyId}${story?.share_token ? `?token=${encodeURIComponent(story.share_token)}` : ""}`, user?.id)}
          type="story"
          storyId={storyId}
        />
      )}

      {/* Share Video Modal */}
      <ShareVideoModal
        isOpen={isShareVideoModalOpen}
        onClose={() => {
          setIsShareVideoModalOpen(false);

          // After share modal closes, show feedback modal for first-time users
          if (user?.id && !feedbackModalCache.hasBeenShown) {
            logger.log(
              "🆕 First-time user: showing full feedback modal after share modal closed",
            );
            setShowFeedbackModal(true);

            // Persist to backend (async, for future sessions/devices)
            logger.log("💾 Adding FEEDBACK_MODAL_SHOWN to users.data_tags...");
            apiClient
              .addUserDataTag(user.id, "FEEDBACK_MODAL_SHOWN")
              .then((result) => {
                if (result.status) {
                  logger.log(
                    "✅ FEEDBACK_MODAL_SHOWN successfully added to users.data_tags",
                  );
                } else {
                  logger.warn(
                    "⚠️ Failed to add to users.data_tags:",
                    result.error,
                  );
                }
              })
              .catch((error) => {
                logger.error("❌ Error adding to users.data_tags:", error);
              });
          }
        }}
        videoUrl={story?.file_url}
        storyTitle={story?.title}
        videoRef={videoRef}
        storyId={storyId}
        user={user}
        subscriptionStatus={subscriptionStatus}
        apiClient={apiClient}
        onShare={() => {
          supabase.from('stories').select('share_count').eq('id', storyId).maybeSingle()
            .then(({ data }) => {
              if (data) supabase.from('stories').update({ share_count: (Number(data.share_count) || 0) + 1 }).eq('id', storyId).then(() => {});
            });
        }}
        onDownload={() => {
          supabase.from('stories').select('download_count').eq('id', storyId).maybeSingle()
            .then(({ data }) => {
              if (data) supabase.from('stories').update({ download_count: (Number(data.download_count) || 0) + 1 }).eq('id', storyId).then(() => {});
            });
        }}
      />

      {/* Feedback Modal - Full version */}
      {user?.id && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          variant="full"
          storyId={storyId}
          contentType="video"
          sessionId={videoSessionId.current}
        />
      )}

      {/* Feedback Modal - Notification version */}
      {user?.id && (
        <FeedbackModal
          isOpen={showFeedbackNotification}
          onClose={() => setShowFeedbackNotification(false)}
          onOpenFullModal={() => {
            setShowFeedbackNotification(false);
            setShowFeedbackModal(true);
          }}
          variant="notification"
          storyId={storyId}
          contentType="video"
          sessionId={videoSessionId.current}
        />
      )}

      <VideoWatermarkProgressModal
        isOpen={isDownloadProgressModalOpen}
        onClose={() => setIsDownloadProgressModalOpen(false)}
        storyId={storyId}
        apiClient={apiClient}
        storyTitle={story?.title || story?.name}
        user={user}
      />

      {/* Paywall Modal for non-premium users */}
      <PaywallModal
        isOpen={isPaywallModalOpen}
        onClose={() => setIsPaywallModalOpen(false)}
        starCost={0}
        currentStars={0}
        contentType="download"
        apiClient={apiClient}
        user={user}
      />
    </div>
  );
};

export default StoryDetail;